import React from 'react'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type SurveyGroupQuestion as SurveyGroupQuestionBase } from '~/gql/graphql'
import { type Unassigned, unassignedSymbol } from '~/utils'

type SurveyGroupQuestion = App.PickGraphQL<
  SurveyGroupQuestionBase,
  'id' | 'name'
>

export type SurveyGroupQuestionSelectorProps<
  TSurveyGroupQuestion extends SurveyGroupQuestion,
> = Omit<
  ItemSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  onChange?: (questionId: TSurveyGroupQuestion['id']) => void
  questions: TSurveyGroupQuestion[]
  value: TSurveyGroupQuestion['id'] | Unassigned
}

export const SurveyGroupQuestionSelector = React.forwardRef(
  <TSurveyGroupQuestion extends SurveyGroupQuestion>(
    {
      onChange,
      questions,
      value: valueProp,
      ...rest
    }: SurveyGroupQuestionSelectorProps<TSurveyGroupQuestion>,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const value = React.useMemo(
      () =>
        questions.find(({ id }) => id === valueProp) ?? {
          id: unassignedSymbol,
        },
      [questions, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (question: TSurveyGroupQuestion) => {
        onChange?.(question.id)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={questions}
        dataItemKey="id"
        textField="name"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
